import React, { useEffect } from 'react';
import PhotoSwipeLightbox from 'photoswipe/lightbox';

import 'photoswipe/style.css';
import './styles.css';
import { hints, rotateLeft, rotateRight } from './icons';

interface IDocumentViewerProps {
  documents?: string[];
  onClose?: () => void;
}

interface PSWPSlide {
  content?: {
    element: HTMLElement;
  };
  _rotation?: number;
}

const ROTATION = 90;
const ROTATED_SCALE = 0.7;

const rotate = (lightbox: PhotoSwipeLightbox, angle: number): void => {
  if (!lightbox.pswp) return;
  const slide = lightbox.pswp.currSlide as PSWPSlide | undefined;
  if (!slide?.content) return;
  const img = slide.content.element as HTMLElement;

  if (!img) return;

  slide._rotation = (slide._rotation || 0) + angle;

  // Apply scale for portrait orientation (90° or 270°)
  const needsScale = slide._rotation % 180 !== 0;
  const scale = needsScale ? ROTATED_SCALE : 1;

  img.style.transition = 'transform 0.3s ease';
  img.style.transformOrigin = 'center center';
  img.style.transform = `rotate(${slide._rotation}deg) scale(${scale})`;
};

export const DocumentViewer = ({ documents, onClose }: IDocumentViewerProps): React.JSX.Element | null => {
  useEffect(() => {
    if (!documents || documents.length === 0) {
      return;
    }

    let lightbox: PhotoSwipeLightbox | null = null;

    // Get image file dimensions - Photoswipe needs these for proper function, the fallback is bad
    const initializeLightbox = async (): Promise<void> => {
      const dataSourcePromises = documents.map(
        src =>
          new Promise<{ src: string; width: number; height: number; msrc: string }>(resolve => {
            const img = new Image();
            img.onload = (): void => {
              resolve({
                src,
                width: img.naturalWidth,
                height: img.naturalHeight,
                msrc: src,
              });
            };
            img.onerror = (): void => {
              // Fallback for failed images
              resolve({ src, width: 1600, height: 1200, msrc: src });
            };
            img.src = src;
          }),
      );

      const dataSource = await Promise.all(dataSourcePromises);

      const options = {
        dataSource,
        clickToCloseNonZoomable: false, // if image is too small to zoom, don't close on click
        indexIndicatorSep: ' of ', // Replaces 1/4 with 1 of 4
        loop: true, // Allow navigation from the last back to first
        pswpModule: (): Promise<typeof import('photoswipe')> => import('photoswipe'),
      };

      lightbox = new PhotoSwipeLightbox(options);
      const lb = lightbox;

      // Add custom UI elements
      lb.on('uiRegister', function () {
        const pswpUI = lb.pswp?.ui;
        if (!pswpUI) return;

        // Button to rotate anticlockwise 90 degrees
        pswpUI.registerElement({
          name: 'rotate-left-button',
          order: 8,
          isButton: true,
          html: {
            isCustomSVG: true,
            inner: rotateLeft,
            outlineID: 'pswp__icn-rotate-left',
          },
          title: 'Rotate anti-clockwise',
          className: 'pswp__button--rotate-left',
          onClick: () => rotate(lb, -ROTATION),
        });

        // Button to rotate clockwise by 90 degrees
        pswpUI.registerElement({
          name: 'rotate-right-button',
          order: 9,
          isButton: true,
          html: {
            isCustomSVG: true,
            inner: rotateRight,
            outlineID: 'pswp__icn-rotate-right',
          },
          title: 'Rotate clockwise',
          className: 'pswp__button--rotate-right',
          onClick: () => rotate(lb, ROTATION),
        });

        // Adds dot buttons for navigation if more than 1 page
        pswpUI.registerElement({
          name: 'bulletsIndicator',
          className: 'pswp__bullets-indicator',
          appendTo: 'wrapper',
          onInit: (el, pswp) => {
            const noItems = pswp.getNumItems();
            if (noItems <= 1) {
              el.classList.add('hidden');
              el.remove();
              return;
            }

            const bullets: HTMLButtonElement[] = [];
            let prevIndex = -1;

            for (let i = 0; i < noItems; i++) {
              const bullet = document.createElement('button');
              bullet.type = 'button';
              bullet.title = `Go to page ${i + 1}`;
              bullet.className = 'pswp__bullet';
              bullet.onclick = (event): void => {
                pswp.goTo(bullets.indexOf(event.target as HTMLButtonElement));
              };
              el.appendChild(bullet);
              bullets.push(bullet);
            }

            pswp.on('change', _a => {
              if (prevIndex >= 0) {
                bullets[prevIndex].classList.remove('pswp__bullet--active');
              }
              bullets[pswp.currIndex].classList.add('pswp__bullet--active');
              prevIndex = pswp.currIndex;
            });
          },
        });

        // Hints & tips dialog
        let hintsEl: HTMLElement | null = null;

        pswpUI.registerElement({
          name: 'hints',
          order: 1,
          appendTo: 'root', // attach to top level, not toolbar
          html: `
      <div class="pswp__hints-dialog" role="dialog" aria-label="Hints and tips">
        <button class="pswp__hints-close" aria-label="Close hints">×</button>
        <div class="pswp__hints-content">
          <h3>Hints &amp; tips</h3>
          <ul>
            <li>Use <b><key>CTRL</key> + Mouse Wheel</b>, click/double-tap, or pinch to zoom.</li>
            <li>Use <b><key>←</key></b> and <b><key>→</key></b> keys to switch pages.</li>
            <li>Find other functions in the top right corner.</li>
            <li>Show these hints &amp; tips any time using the question mark icon.</li>
          </ul>
        </div>
      </div>
    `,
          onInit: el => {
            hintsEl = el as HTMLElement;
            const hEl = hintsEl;

            const closeBtn = hEl.querySelector('.pswp__hints-close');
            closeBtn?.addEventListener('click', () => {
              hEl.classList.add('hidden');
              localStorage.setItem('viewer-hints-hidden', 'true');
            });

            // check if dismissed
            if (localStorage.getItem('viewer-hints-hidden') === 'true') {
              hEl.classList.add('hidden');
            }
          },
        });

        // Toggle hints dialog button
        pswpUI.registerElement({
          name: 'show-hints',
          order: 11,
          isButton: true,
          html: {
            isCustomSVG: true,
            inner: hints,
            outlineID: 'pswp__icn-hints',
          },
          title: 'Show hints & tips',
          onClick: () => {
            if (!hintsEl) return;
            const isHidden = hintsEl.classList.contains('hidden');
            hintsEl.classList.toggle('hidden', !isHidden);
            localStorage.setItem('viewer-hints-hidden', isHidden ? 'false' : 'true');
          },
        });
      });

      // Adds an aria-label to the dialog for WCAG compliance
      lb.on('afterInit', () => {
        const dialogEl = lb.pswp?.element; // The main dialog container
        if (dialogEl) {
          dialogEl.setAttribute('aria-label', 'Document viewer');
        }
      });

      if (onClose) {
        lightbox.on('close', () => {
          onClose();
        });
      }

      lightbox.init();

      lightbox.loadAndOpen(0); // Opens images directly rather than from a gallery
    };

    initializeLightbox();

    return (): void => {
      if (lightbox) {
        lightbox.destroy();
        lightbox = null;
      }
    };
  }, [documents, onClose]);

  return null;
};
